import { FC, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './sign-up.less';
import { ReactComponent as Logo } from '../assets/logo.svg';
import SignupForm from '../components/SignupForm/SignupForm';
import { SIGNUP_PARAMS } from '../constants/auth';
import { useTheme } from '../contexts/ThemeContext';
import Meta from '../components/Meta';
import {
  captureReferrerData,
  getStoredReferrerData,
  buildAuth0UTMParams,
} from '../utils/utmTracking';
import {
  trackPageView,
  trackEvent,
  sessionId,
  correlationId,
} from '../utils/monitoring';
import { trackSignupStarted } from '../services/analyticsService';

// Declare Facebook Pixel types
declare global {
  interface Window {
    fbq?: (action: string, ...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

const SignUp: FC = () => {
  const { loginWithRedirect } = useAuth0();
  const { setThemeMode } = useTheme();

  // Force light theme for this page
  useEffect(() => {
    // Store original theme
    const originalTheme = localStorage.getItem('theme') || 'system';

    // Force light theme
    setThemeMode('light');
    document.documentElement.setAttribute('data-theme', 'light');

    return () => {
      // Restore original theme when component unmounts
      setThemeMode(originalTheme as 'light' | 'dark' | 'system');
      if (originalTheme === 'system') {
        const prefersDark = window.matchMedia(
          '(prefers-color-scheme: dark)'
        ).matches;
        document.documentElement.setAttribute(
          'data-theme',
          prefersDark ? 'dark' : 'light'
        );
      } else {
        document.documentElement.setAttribute('data-theme', originalTheme);
      }
    };
  }, [setThemeMode]);

  // Enable scrolling for this page (override global overflow: hidden)
  useEffect(() => {
    // Store original body overflow
    const originalOverflow = document.body.style.overflow;

    // Enable scrolling
    document.body.style.overflow = 'auto';

    return () => {
      // Restore original overflow when component unmounts
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Capture UTM parameters and track page view in Azure App Insights
  useEffect(() => {
    // Capture referrer and UTM data when page loads
    const referrerData = captureReferrerData();

    // Track signup page view with UTM parameters
    trackPageView('Signup Page', window.location.href, {
      ...referrerData,
    });

    // Also track as a custom event for easier querying in App Insights
    trackEvent('Signup Page Visited', {
      page: '/sign-up',
      ...referrerData,
    });
  }, []);

  // Initialize Facebook Pixel (runs once on mount)
  useEffect(() => {
    // Check if pixel is already initialized to prevent duplicates
    if (window.fbq) {
      console.log('[SignUp] Facebook Pixel already initialized, skipping');
      return;
    }

    /* eslint-disable @typescript-eslint/no-explicit-any, prefer-rest-params, prefer-spread */
    (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      'script',
      'https://connect.facebook.net/en_US/fbevents.js'
    );
    /* eslint-enable @typescript-eslint/no-explicit-any, prefer-rest-params, prefer-spread */

    // Initialize pixel - the fbq function queues calls until the script loads
    // The IIFE above creates window.fbq, so it's guaranteed to exist here
    const fbq = window.fbq as unknown as (
      action: string,
      ...args: unknown[]
    ) => void;
    console.log('[SignUp] Initializing Facebook Pixel');
    fbq('init', '885421030601906');
    fbq('track', 'PageView');
  }, []);

  const handleEmailSubmit = (email: string) => {
    // Get stored UTM and referrer data
    const referrerData = getStoredReferrerData();

    // Store the submitted email for use on the verify-email page
    // This ensures we show the correct email even if Auth0 has a cached session
    localStorage.setItem('signup_email', email);

    // Store session IDs for tracking signup journey
    if (sessionId) {
      localStorage.setItem('signup_session_id', sessionId);
    }
    if (correlationId) {
      localStorage.setItem('signup_correlation_id', correlationId);
    }

    // Track form submission with UTM context (without PII)
    // Note: user_id will be null here since user hasn't authenticated yet
    // We'll track with user_id after Auth0 callback
    trackEvent('Signup Form Submitted', {
      email_domain: email.split('@')[1], // Track domain for B2B insights (not PII)
      ...(referrerData ?? {}),
    });

    // Send email to Segment via backend (fire-and-forget, never blocks Auth0 redirect)
    trackSignupStarted(
      email,
      correlationId || crypto.randomUUID(),
      referrerData
    );

    // Pass UTM parameters to Auth0 via appState
    const signupAttribution = {
      ...(referrerData ?? {}),
      landing_page: window.location.href,
      timestamp: new Date().toISOString(),
    };

    console.log(
      '[SignUp] Passing signup_attribution to Auth0:',
      signupAttribution
    );

    loginWithRedirect({
      appState: {
        returnTo: '/',
        signup_attribution: signupAttribution,
      },
      authorizationParams: {
        screen_hint: 'signup',
        login_hint: email,
        signup_params: SIGNUP_PARAMS,
        prompt: 'login',
        max_age: 0,
        // Pass UTM and referrer data to Auth0 for tracking in Actions
        ...buildAuth0UTMParams(referrerData),
      },
    });
  };

  return (
    <>
      <Meta name="Cirra AI free trial signup" />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src="https://www.facebook.com/tr?id=885421030601906&ev=PageView&noscript=1"
          alt=""
        />
      </noscript>
      <div id="signup-container">
        <div className="leftPanel">
          <div className="content">
            <Logo className="logo" />
            <h1>Meet Your AI Salesforce Admin!</h1>
            <p className="subtitle">
              Get started for free -- no credit card required.
            </p>
            <div className="features">
              <div className="feature">
                <span>✓</span>
                <p>Access to the Salesforce Admin MCP Server</p>
              </div>
              <div className="feature">
                <span>✓</span>
                <p>15 day trial with extra credits</p>
              </div>
              <div className="feature">
                <span>✓</span>
                <p>Subscribe after your trial or continue on the free plan</p>
              </div>
            </div>
            <p className="subtitle">
              <span style={{ color: 'white' }}>
                Any questions? Contact us{' '}
                <a
                  href="https://cirra.ai/contact-us"
                  target="_blank"
                  style={{
                    color: 'white',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    textDecorationThickness: '2px',
                    textDecorationColor: 'white',
                  }}
                  rel="noreferrer"
                >
                  here
                </a>{' '}
                or by email at support@cirra.ai!
              </span>
            </p>
            <p className="subtitle">
              <span style={{ color: 'white' }}>
                Want to see a demo first? Book it{' '}
                <a
                  href="https://cirra.ai/book-a-demo"
                  target="_blank"
                  style={{
                    color: 'white',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                    textDecorationThickness: '2px',
                    textDecorationColor: 'white',
                  }}
                  rel="noreferrer"
                >
                  here
                </a>
                .
              </span>
            </p>
          </div>
        </div>
        <div className="rightPanel">
          <div className="previewContainer">
            <div className="previewCard">
              <Logo className="previewLogo" />
              <h2>Welcome</h2>
              <SignupForm onEmailSubmit={handleEmailSubmit} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUp;
